import { unionBy } from 'lodash';

import { FOLDER_FILE_KEY } from 'src/constants/fileConsts';
import {
  FileChannel,
  FilesActions,
  FilesActionTypes,
  FilesState,
} from 'src/store/files/types';
import FileUtils from 'src/utils/FileUtils';

// Helper to get the effective name for a file/link item
// - For files: extract from fileKey (e.g., "files/timestamp/document.pdf" -> "document.pdf")
// - For links: use fields.name or fields.fileName if renamed
const getEffectiveName = (
  fileKey: string | undefined,
  fileName: string | undefined,
  name: string | undefined,
): string => {
  // Prefer fileName if set (renamed item)
  if (fileName) return fileName;
  // For links, use the name field
  if (name) return name;
  // For files, extract from fileKey
  if (fileKey && fileKey !== FOLDER_FILE_KEY) {
    return fileKey.split('/').pop() || '';
  }
  return '';
};

const initialState: FilesState = {
  items: [],
  loading: false,
  loaded: false,
  saving: false,
  saved: false,
  downloading: false,
  error: '',
  renaming: false,
  moving: false,
  channels: [],
  fileDragging: false,
  hoveredRowNode: null,
  helperActions: {
    actionName: null,
  },
  channelsLoaded: false,
  duplicateConflict: null,
};

const FilesReducer = (state = initialState, action: FilesActions) => {
  switch (action.type) {
    case FilesActionTypes.GET_FILES:
      return {
        ...state,
        loading: true,
        loaded: false,
        items: [], // clear exsiting items when getting new items
      };

    case FilesActionTypes.GET_FILES_ERROR:
      return {
        ...state,
        loading: false,
        loaded: false,
        error: action.error,
      };
    case FilesActionTypes.GET_FILES_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        items: action.payload,
      };
    case FilesActionTypes.SAVE_UPLOADED_FILES:
      return {
        ...state,
        saving: true,
        saved: false,
      };
    case FilesActionTypes.SAVE_UPLOADED_FILES_SUCCESS: {
      // When replacing files/folders (duplicate_file_name feature flag enabled),
      // remove old items from state before adding new ones.
      // When feature flag is off or behavior is 'append', just append new files.
      let filteredItems = state.items;
      if (action.duplicateBehavior === 'replace') {
        // Filter out replaced folders and their nested contents
        if (
          action.replacedFolderPaths &&
          action.replacedFolderPaths.length > 0
        ) {
          filteredItems = filteredItems.filter((item) => {
            const itemPath = item.fields.path || '';
            // Remove items whose path matches or is nested under any replaced folder
            return !action.replacedFolderPaths?.some(
              (folderPath) =>
                itemPath === folderPath ||
                itemPath.startsWith(folderPath + '/'),
            );
          });
        }

        // Filter out replaced files/links (non-folders) by matching path + name
        // Backend detects duplicates by fileKey basename for files, or name field for links
        if (action.replacedFilePaths && action.replacedFilePaths.length > 0) {
          filteredItems = filteredItems.filter((item) => {
            // Skip folders - they're handled separately
            if (item.fields.fileKey === FOLDER_FILE_KEY) return true;

            // Get effective name for comparison
            // - For files: fileKey basename
            // - For links: fields.name or fields.fileName if renamed
            const itemName = getEffectiveName(
              item.fields.fileKey,
              item.fields.fileName,
              (item.fields as { name?: string })?.name,
            );

            // Get fileKey basename for path extraction logic
            const itemFileKeyBasename =
              item.fields.fileKey?.split('/').pop() ?? '';

            // Determine the folder path for this item
            // For links: path is always just the folder path
            // For files: path format is inconsistent - sometimes includes filename, sometimes not
            let itemFolderPath = '';
            if (item.fields.path) {
              const pathEndsWithFileKey =
                itemFileKeyBasename &&
                (item.fields.path === itemFileKeyBasename ||
                  item.fields.path.endsWith('/' + itemFileKeyBasename));

              if (pathEndsWithFileKey) {
                // Path includes filename, extract folder portion
                itemFolderPath = FileUtils.getEffectiveFolderPath(
                  item.fields.path,
                  itemFileKeyBasename,
                );
              } else {
                // Path is just the folder path (links, or files where path doesn't include filename)
                itemFolderPath = item.fields.path;
              }
            }

            // Remove items that match any replaced file's folder path + name
            return !action.replacedFilePaths?.some(
              (replacedFile) =>
                itemFolderPath === replacedFile.path &&
                itemName === replacedFile.fileName,
            );
          });
        }
      }
      return {
        ...state,
        saving: false,
        saved: true,
        items: [...filteredItems, ...action.payload],
      };
    }
    case FilesActionTypes.SAVE_UPLOADED_FILES_ERROR:
      return {
        ...state,
        loading: false,
        loaded: false,
        error: action.error,
      };

    case FilesActionTypes.UNSELECT_CHANNEL:
      return {
        ...state,
        items: [],
        selectedChannel: undefined,
        loading: false,
        loaded: false,
      };
    case FilesActionTypes.DELETE_FILES: {
      const files = state.items.filter(
        (file) => !action.payload.includes(file.id),
      );
      return {
        ...state,
        loading: false,
        loaded: true,
        items: files,
      };
    }
    case FilesActionTypes.DELETE_FOLDER: {
      const folder = state.items.find((file) => file.id === action.payload);
      if (folder) {
        // filter out all files inside folder
        const folderPath = folder.fields.path || '';
        const files = folderPath
          ? state.items.filter((f) => {
              return !(
                f.id === folder.id ||
                (f.ownerId === folder.ownerId &&
                  f.fields.path?.startsWith(folderPath))
              );
            })
          : state.items;
        return {
          ...state,
          loading: false,
          loaded: true,
          items: files,
        };
      }
      return state;
    }
    case FilesActionTypes.SELECT_FILES_CHANNEL:
      return {
        ...state,
        loading: false,
        loaded: false,
        selectedChannel: action.payload,
      };
    case FilesActionTypes.CLEAR_FILES:
      return {
        ...initialState,
      };
    case FilesActionTypes.DOWNLOAD_FILE:
      return {
        ...state,
        downloading: true,
      };
    case FilesActionTypes.DOWNLOAD_FILE_SUCCESS:
      return {
        ...state,
        downloading: false,
      };
    case FilesActionTypes.DOWNLOAD_FILE_ERROR:
      return {
        ...state,
        downloading: false,
        error: action.error,
      };
    case FilesActionTypes.UPDATE_FILE_FIELDS_REQUEST:
      return {
        ...state,
        renaming: true,
      };
    case FilesActionTypes.UPDATE_FILE_FIELDS_SUCCESS: {
      const filesStaged = state.items.map((file) => {
        if (action.successIds.includes(file.id)) {
          const currentUpdatedFile = action.updatedFiles.find(
            (updatedFile) => updatedFile.id === file.id,
          );
          if (currentUpdatedFile) {
            return {
              ...file,
              fields: {
                ...file.fields,
                esignStatus: currentUpdatedFile.fields.esignStatus,
                lastEsigModifierIdentityID:
                  currentUpdatedFile.fields.lastEsigModifierIdentityID,
                components: currentUpdatedFile.fields.components,
                fileName: currentUpdatedFile.fields.fileName,
                starred: currentUpdatedFile.fields.starred,
                path: currentUpdatedFile.fields.path,
                status: currentUpdatedFile.fields.status,
              },
              clientPermissions: currentUpdatedFile.clientPermissions,
            };
          }
          return file;
        }
        return file;
      });

      return {
        ...state,
        items: filesStaged,
        renaming: false,
      };
    }

    case FilesActionTypes.UPDATE_FILE_FIELDS_FAILURE:
      return {
        ...state,
        renaming: false,
        error: action.error,
      };

    case FilesActionTypes.MOVE_FOLDER_FAILURE:
      return {
        ...state,
        moving: false,
        error: action.error,
      };
    case FilesActionTypes.MOVE_FOLDER_REQUEST:
      return {
        ...state,
        moving: true,
      };
    case FilesActionTypes.MOVE_FOLDER_SUCCESS: {
      const filesStaged = unionBy(action.payload, state.items, 'id');

      return {
        ...state,
        items: filesStaged,
        moving: false,
      };
    }
    case FilesActionTypes.SET_FILE_CHANNELS: {
      return {
        ...state,
        channels: action.channels
          .filter((chan) => chan.entityStatus !== 'ARCHIVED')
          // set channels sorted by created date - this is useful
          // in case we want to select the next selected
          // channel after deleting a channel. We can
          // select the first channel in the list
          // which will be the most recently created
          .sort((a, b) => {
            const aDate = new Date(a.createdDate);
            const bDate = new Date(b.createdDate);
            return bDate.getTime() - aDate.getTime();
          }),
        channelsLoaded: true,
      };
    }
    case FilesActionTypes.ADD_FILE_CHANNELS: {
      const { addFileChannelOptions, channels } = action;
      const { selectFirstChannel } = addFileChannelOptions || {};
      const newChannelIDs = channels
        .filter((chan) => chan.entityStatus !== 'ARCHIVED')
        .map((chan) => chan.id);
      const previousChannels = state.channels.filter(
        (chan) => !newChannelIDs.includes(chan.id),
      );
      return {
        ...state,
        channels: [...previousChannels, ...action.channels],
        ...(selectFirstChannel
          ? {
              selectedChannel: {
                id: action.channels[0].id,
                additionalFields: action.channels[0].additionalFields,
                fields: action.channels[0].fields,
              },
            }
          : {}),
        ...(selectFirstChannel ? { loading: false, loaded: false } : {}),
      };
    }
    case FilesActionTypes.REMOVE_FILE_CHANNELS: {
      const deletedChannelIDs = action.channels.map((chan) => chan.id);
      const filteredChannels = state.channels.filter(
        (chan) => !deletedChannelIDs.includes(chan.id),
      );
      return {
        ...state,
        channels: filteredChannels,
      };
    }
    case FilesActionTypes.INSERT_FILES: {
      return {
        ...state,
        saving: false,
        saved: true,
        items: [...state.items, ...action.payload],
      };
    }
    case FilesActionTypes.UPDATE_FILE_CHANNELS: {
      const { updatedChannels } = action;
      const updatedChannelIds = updatedChannels.map((c) => c.id);
      const { channels: existingChannels } = state;

      const otherChannels = existingChannels.filter(
        (channel: FileChannel) => !updatedChannelIds.includes(channel.id),
      );
      const updatedSelectedChannels = updatedChannels.filter(
        (c) => c.id === state.selectedChannel?.id,
      );

      const newChannels = updatedChannels
        .concat(otherChannels)
        .filter((chan) => chan.entityStatus !== 'ARCHIVED');
      return {
        ...state,
        channels: newChannels,
        selectedChannel:
          updatedSelectedChannels && updatedSelectedChannels.length > 0
            ? updatedSelectedChannels.at(0)
            : state.selectedChannel,
      };
    }
    case FilesActionTypes.UPDATE_FILE_CHANNEL: {
      const { channelId, updates } = action;
      const updatedChannels = state.channels.map((channel) =>
        channel.id === channelId ? { ...channel, ...updates } : channel,
      );
      const updatedSelectedChannel =
        state.selectedChannel?.id === channelId
          ? { ...state.selectedChannel, ...updates }
          : state.selectedChannel;

      return {
        ...state,
        channels: updatedChannels,
        selectedChannel: updatedSelectedChannel,
      };
    }
    case FilesActionTypes.CANCEL_ESIGNATURE_REQUEST_SUCCESS: {
      const updatedFileEntity = action.payload;
      return {
        ...state,
        items: state.items.map((item) =>
          item.id === updatedFileEntity.id ? updatedFileEntity : item,
        ),
      };
    }
    case FilesActionTypes.UPDATE_FILE_DRAGGING: {
      return {
        ...state,
        fileDragging: action.payload,
      };
    }
    case FilesActionTypes.UPDATE_HOVERED_ROW_NODE: {
      return {
        ...state,
        hoveredRowNode: action.payload,
      };
    }
    case FilesActionTypes.UPDATE_FILES_HELPER_ACTION: {
      return {
        ...state,
        helperActions: {
          ...state.helperActions,
          actionName: action.payload,
        },
      };
    }
    case FilesActionTypes.DELETE_FILE_CHANNEL: {
      const { channelId } = action;
      const filteredChannels = state.channels.filter(
        (chan) => channelId !== chan.id,
      );

      const selectedChannel =
        state.selectedChannel && state.selectedChannel.id === channelId
          ? undefined
          : state.selectedChannel;

      return {
        ...state,
        channels: filteredChannels,
        selectedChannel,
      };
    }
    case FilesActionTypes.DUPLICATE_CONFLICT_DETECTED: {
      return {
        ...state,
        duplicateConflict: action.payload,
        saving: false,
      };
    }
    case FilesActionTypes.CLEAR_DUPLICATE_CONFLICT: {
      return {
        ...state,
        duplicateConflict: null,
      };
    }
    default:
      return state;
  }
};

export default FilesReducer;
